import {
  onCleanup,
  For,
  children,
  on,
  createMemo,
  createComputed,
  untrack,
  batch,
  createRoot,
  createEffect,
  onMount,
  createSignal,
} from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/+esm';
import { createStore } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/store/+esm';
import { insert } from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/web/+esm';
import {
  register,
  createContext,
  withProvider,
  withConsumer,
  compose,
} from 'https://cdn.jsdelivr.net/npm/component-register@0.6.7/+esm';
import html from 'https://cdn.jsdelivr.net/npm/solid-js@1.7.3/html/+esm';
import { animate } from 'https://cdn.jsdelivr.net/npm/motion@10.16.2/+esm';

// Utility custom element functions

function nextFrame(fn) {
  requestAnimationFrame(() => {
    requestAnimationFrame(fn);
  });
}

export const Transition = (props) => {
  let el;
  let first = true;
  const [s1, set1] = createSignal();
  const [s2, set2] = createSignal();
  const resolved = children(() => props.children);

  const { onBeforeEnter, onEnter, onAfterEnter, onBeforeExit, onExit, onAfterExit } = props;

  const classnames = createMemo(() => {
    const name = props.name || 's';
    return {
      enterActiveClass: props.enterActiveClass || name + '-enter-active',
      enterClass: props.enterClass || name + '-enter',
      enterToClass: props.enterToClass || name + '-enter-to',
      exitActiveClass: props.exitActiveClass || name + '-exit-active',
      exitClass: props.exitClass || name + '-exit',
      exitToClass: props.exitToClass || name + '-exit-to',
    };
  });

  function enterTransition(el, prev) {
    if (!first || props.appear) {
      const enterClasses = classnames().enterClass.split(' ');
      const enterActiveClasses = classnames().enterActiveClass.split(' ');
      const enterToClasses = classnames().enterToClass.split(' ');
      onBeforeEnter && onBeforeEnter(el);
      el.classList.add(...enterClasses);
      el.classList.add(...enterActiveClasses);
      nextFrame(() => {
        el.classList.remove(...enterClasses);
        el.classList.add(...enterToClasses);
        onEnter && onEnter(el, () => endTransition());
        if (!onEnter || onEnter.length < 2) {
          el.addEventListener('transitionend', endTransition);
          el.addEventListener('animationend', endTransition);
        }
      });

      function endTransition(e) {
        if (el && (!e || e.target === el)) {
          el.removeEventListener('transitionend', endTransition);
          el.removeEventListener('animationend', endTransition);
          el.classList.remove(...enterActiveClasses);
          el.classList.remove(...enterToClasses);
          batch(() => {
            s1() !== el && set1(el);
            s2() === el && set2(undefined);
          });
          onAfterEnter && onAfterEnter(el);
          if (props.mode === 'inout') exitTransition(el, prev);
        }
      }
    }
    prev && !props.mode ? set2(el) : set1(el);
  }

  function exitTransition(el, prev) {
    const exitClasses = classnames().exitClass.split(' ');
    const exitActiveClasses = classnames().exitActiveClass.split(' ');
    const exitToClasses = classnames().exitToClass.split(' ');
    if (!prev.parentNode) return endTransition();
    onBeforeExit && onBeforeExit(prev);
    prev.classList.add(...exitClasses);
    prev.classList.add(...exitActiveClasses);
    nextFrame(() => {
      prev.classList.remove(...exitClasses);
      prev.classList.add(...exitToClasses);
    });
    onExit && onExit(prev, () => endTransition());
    if (!onExit || onExit.length < 2) {
      prev.addEventListener('transitionend', endTransition);
      prev.addEventListener('animationend', endTransition);
    }

    function endTransition(e) {
      if (!e || e.target === prev) {
        prev.removeEventListener('transitionend', endTransition);
        prev.removeEventListener('animationend', endTransition);
        prev.classList.remove(...exitActiveClasses);
        prev.classList.remove(...exitToClasses);
        s1() === prev && set1(undefined);
        onAfterExit && onAfterExit(prev);
        if (props.mode === 'outin') enterTransition(el, prev);
      }
    }
  }

  createComputed((prev) => {
    el = resolved();
    while (typeof el === 'function') el = el();
    return untrack(() => {
      if (el && el !== prev) {
        if (props.mode !== 'outin') enterTransition(el, prev);
        else if (first) set1(el);
      }
      if (prev && prev !== el && props.mode !== 'inout') exitTransition(el, prev);
      first = false;
      return el;
    });
  });

  return [s1, s2];
};

function getContext(element) {
  const elementContextSymbol = Object.getOwnPropertySymbols(element).find((s) => s.description === 'element-context');
  const contextSymbol = Object.getOwnPropertySymbols(element[elementContextSymbol]).find(
    (s) => s.description === 'context'
  );
  return element[elementContextSymbol][contextSymbol];
}

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get() {
        return get();
      },
      set(v) {
        set(() => v);
      },
    });
  }
  return props;
}

function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}

function withSolid(ComponentType) {
  return (rawProps, options) => {
    const { element } = options;
    return createRoot((dispose) => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => (props[key] = val));
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = '';
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}

function withNoShadowDOM(ComponentType = (props, options) => {}) {
  return (props, options) => {
    const { element } = options;
    element.renderRoot = element;
    return ComponentType(props, options);
  };
}

const customShadowlessElement = (tagName, props = {}, Component = (props, options) => {}, ...rest) =>
  compose(register(tagName, props), withNoShadowDOM, withSolid, ...rest)(Component);

const ShowMoreContext = createContext((state = { restChildren: [], isShowingMore: false, trigger: null }) => {
  return createStore(state);
});

function ShowMoreBlock(props, { element }) {
  const [context, setContext] = getContext(element);
  const data = JSON.parse(Array.from(element.children).find((child) => child.type === 'application/json').textContent);
  const trigger = element.querySelector('show-more-trigger');

  function syncData() {
    batch(() => {
      element.setAttribute('is-showing-more', context.isShowingMore ? 'is-showing-more' : 'is-not-showing-more');
      setContext('restChildren', data?.restChildren || []);
      setContext('initialChildren', data?.initialChildren || []);
      setContext('isShowingMore', props.isShowingMore === 'is-showing-more');
      setContext('trigger', trigger);
    });
  }

  createEffect(syncData);
}

function beforeEnter(el) {
  el.style.cssText = `
    transform: scale(.5);
    opacity: 0;
    display: inline-flex;
  `;
}

function afterExit(el) {
  el.style.cssText = `
    transform: scale(.5);
    opacity: 0;
    display: none;
  `;
}

function ShowMoreContent(props, { context, element }) {
  const [state, _] = context;
  const staggerAmount = 0.05;
  const transition = { duration: 0.35, opacity: { duration: 0.2 } };
  const calculateStagger = (i, reverse = false) =>
    reverse ? state.restChildren.length * staggerAmount - i * staggerAmount : i * staggerAmount;

  const popIn = (el, i) =>
    animate(el, { opacity: [null, 1], scale: [0.5, 1] }, { ...transition, delay: calculateStagger(i) });
  const popOut = (el, i) =>
    animate(el, { opacity: [null, 0], scale: [1, 0.5] }, { ...transition, delay: calculateStagger(i, true) });

  element.replaceChildren([]);

  onMount(() => {
    element.append(state.trigger);
  });

  function More(props) {
    let ref = null;

    onMount(() => {
      afterExit(ref);
    });

    createEffect(
      on(
        () => state.isShowingMore,
        (isShowingMore) => {
          if (!ref) return;

          if (isShowingMore) {
            beforeEnter(ref);
            const cleanup = popIn(ref, props.index);
            return onCleanup(cleanup.finish);
          } else {
            const cleanup = popOut(ref, props.index);
            cleanup.finished.then(() => afterExit(ref));
            return onCleanup(cleanup.finish);
          }
        }
      )
    );

    return html`<div ref=${(el) => (ref = el)} style="display: inline-flex;" innerHTML=${() => props.child}></div>`;
  }

  return html`
    <${For} each=${() => state.initialChildren}>
      ${(child) => html`<div style="display: inline-flex;" innerHTML=${child}></div>`}
    <//>
    <${For} each=${() => state.restChildren}> ${(child, i) => html` <${More} index=${i} child=${child} /> `} <//>
  `;
}

function ShowMoreButton(props, { context, element }) {
  const [state, setState] = context;

  element.setAttribute('role', 'button');

  createEffect(() => {
    element.ariaPressed = state.isShowingMore ? 'true' : 'false';
  });

  onMount(() => {
    function toggleShowMore(e) {
      setState('isShowingMore', !state.isShowingMore);
    }

    element.addEventListener('click', toggleShowMore);

    onCleanup(() => {
      element.removeEventListener('click', toggleShowMore);
    });
  });
}

customShadowlessElement('show-more-block', { isShowingMore: false }, ShowMoreBlock, withProvider(ShowMoreContext));
customShadowlessElement('show-more-trigger', {}, ShowMoreButton, withConsumer(ShowMoreContext, 'context'));
customShadowlessElement('show-more-content', {}, ShowMoreContent, withConsumer(ShowMoreContext, 'context'));
